import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { Select, Store } from '@ngxs/store';
import isEqual from 'lodash-es/isEqual';
import remove from 'lodash-es/remove';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, startWith, tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import {
    SharedDaysInMonthEditComponent
} from 'src/app/_shared/components/shared-days-in-month-edit/shared-days-in-month-edit.component';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { AllocationsContext } from 'src/app/_shared/services/allocations/detail/allocation.service';
import { SharedGetReferenceMotorDatesListAction } from 'src/app/_shared/stores/reference-years/reference-years.action';
import { ReferenceYearsState } from 'src/app/_shared/stores/reference-years/reference-years.state';
import {
    SharedSubventionCriteriaState
} from 'src/app/_shared/stores/shared-subvention-criteria/shared-subvention-criteria-state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { deleteEmptyProperties, isEmpty } from 'src/app/utils/object.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';
import {
    ArrayOfanyType,
    ArrayOfMoteurRegleTypeDateRefDto,
    ChampDto,
    ModeleRegleSubQuotaModeleDto,
    MoteurRegleTypeDateRefDto,
    RegleSubQuotaListeValModelDto
} from 'src/graphql/generated-types';

type NormalizeType = ModeleRegleSubQuotaModeleDto & { IdAnneeReference: number, ListeValeursDefault: ArrayOfanyType };

@Component({
    selector: 'app-shared-allocation-edit-envelope',
    templateUrl: './shared-allocation-edit-envelope.component.html',
    styleUrls: ['./shared-allocation-edit-envelope.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAllocationEditEnvelopeComponent),
    standalone: false
})
export class SharedAllocationEditEnvelopeComponent extends CustomFormControlAccess implements OnInit {

    referenceMotorDatesList$: Observable<MoteurRegleTypeDateRefDto[]> = inject(Store).select(ReferenceYearsState.referenceMotorDatesList);

    @Input()
    type: string;

    @Coerce('boolean')
    @Input()
    isNominativeControlDisplayed = false;

    form: FormGroup;

    criterion: ChampDto = null;

    controls = [];

    displayedColumns = [];

    readonly columns: KalTableHeader<RegleSubQuotaListeValModelDto & { Valeur: AbstractControl }>[] = [
        {code: 'Valeur', label: 'Valeur'},
        {code: 'De', label: 'De'},
        {code: 'A', label: 'A'},
        {code: 'Od', label: 'Ouvrant Droit'},
        {code: 'Conjoint', label: 'Conjoint'},
        {code: 'Enfant', label: 'Enfant'},
        {code: 'Externe', label: 'Externe'},
        {code: 'ValMin', label: 'Valeur min'},
        {code: 'ValMax', label: 'Valeur max'}
    ];

    private readonly sharedDisplayedColumns = [
        'Od',
        'Conjoint',
        'Enfant',
        'Externe',
        'ValMin',
        'ValMax'
    ];

    displayChildrenSelectedDateField = false;
    displayAddLine = false;

    referenceMotorDatesList: MoteurRegleTypeDateRefDto[] = [];

    AllocationsContext = AllocationsContext;

    constructor(private readonly fb: FormBuilder,
                private readonly store: Store,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    get isFixe(): boolean {
        return this.form ? this.form.value.IsFixe : false;
    }

    get rulesList(): FormArray {
        return this.form.get('ListeRegle') as FormArray;
    }

    get defaultRulesList(): FormArray {
        return this.form.get('defaultValues') as FormArray;
    }

    get criteriaList(): ChampDto[] {
        return this.store.selectSnapshot(SharedSubventionCriteriaState.criteriaList);
    }

    get hasAgeValue(): boolean {
        return this.form.get('handleAge').value;
    }

    get hasSubventionsError(): boolean {
        return this.form.getError('invalidsubventions');
    }

    get subventionsError(): string {
        return this.isArticle ? 'Au moins une quantité doit être renseignée' : 'Au moins un des montants doit être renseigné';
    }

    get inputIcon(): string {
        return this.isArticle ? '' : 'euro';
    }

    get inputType(): string {
        return this.isArticle ? 'number' : 'currency';
    }

    private get isArticle(): boolean {
        return this.type === AllocationsContext.ART;
    }

    getDefaultControl(controlName: string): FormControl {
        return this.defaultRulesList.at(0).get(controlName) as FormControl;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable({emitEvent: false});
        } else {
            this.form.enable({emitEvent: false});
        }
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        return of(this.form.valid ? null : {envelopeerrors: true}).pipe(this.untilDestroyed);
    }

    writeValue(value: ModeleRegleSubQuotaModeleDto & { ListeRegle, ListeValeursDefault }): void {
        if (!value) {
            return;
        }

        let defaultValues = {};
        const {
            DateRefPondJour,
            DateRefPondMois,
            ...ruleValues
        } = value;

        if (!!ruleValues?.IdChamp) {
            defaultValues = remove(ruleValues?.ListeValeursDefault,
                ({ChpLstValId, De, A}) => !ChpLstValId && !De && !A)[0] || {};

            this.criterion = this.criterion ?? this.criteriaList.find(c => c.Id === ruleValues.IdChamp);
            if (this.criterion?.ChampTypeDonnee?.Code === environment.constants.fieldsTypesCodes.LST) {
                this.criterion.ListChampListeValeur.ChampListeValeurDTO.filter(({IsCloture}) => !IsCloture).forEach(field => {
                    if (ruleValues.ListeRegle.every(rule => rule.ChpLstValId !== field.Id)) {
                        ruleValues.ListeRegle.push({ChpLstValId: field.Id});
                    }
                });
            }
        }

        const hasChildrenAges = !!ruleValues.RglModeleIdAgeEnfMin || !!ruleValues.RglModeleIdAgeEnfMax;
        this.form.patchValue({
            ...ruleValues,
            IsFixe: !ruleValues.IdChamp,
            handleAge: hasChildrenAges,
            DateRefPond: hasChildrenAges
                ? {
                    day: DateRefPondJour,
                    month: DateRefPondMois
                }
                : null
        }); // emit volontaire

        this.defaultRulesList.clear();
        this.rulesList.clear();

        this.defaultRulesList.push(this.generateRulesListFormGroup(defaultValues));
        if (arrayNotEmpty(ruleValues.ListeRegle)) {
            (ruleValues.ListeRegle).forEach(rule => this.rulesList.push(this.generateRulesListFormGroup(rule)));
        } else {
            this.rulesList.push(this.generateRulesListFormGroup({}));
        }

        this.controls = this.getControls();
        this.displayedColumns = this.getDisplayedColumns();
        super.writeValue(value);
    }

    getRuleName(id: number): string {
        if (!this.criterion?.ListChampListeValeur) {
            return '';
        }
        return this.criterion.ListChampListeValeur.ChampListeValeurDTO.find(({Id}) => id === Id)?.Valeur || '';
    }

    addLine(): void {
        this.rulesList.push(this.generateRulesListFormGroup({}));
        this.controls = this.getControls();
    }

    deleteLine(idx: number): void {
        this.rulesList.removeAt(idx);
        if (this.rulesList.length === 0) {
            this.rulesList.push(this.generateRulesListFormGroup({}));
        }
        this.controls = this.getControls();
    }

    initSubscriptions(): void {
        this.watch$ = this.getReferenceMotorDatesList$().pipe(
            tap(() => this.cdr.markForCheck()),
            filter(() => !this.form.get('IdTypeDateRefPond').value),
            tap(() => {
                if (!this.form.get('handleAge').value) {
                    this.form.patchValue({
                        IdTypeDateRefPond: this.referenceMotorDatesList.find(e => e.Code === 'OPE')?.Id || null
                    });
                }

                this.notifyUpdate(this.normalize(this.form.value));
                this.cdr.markForCheck();
            })
        );

        this.watch$ = this.form.valueChanges.pipe(
            tap(value => this.notifyUpdate(this.normalize(value))),
        );

        // réinitialisation du tableau des règles selon critères lorsque celui ci change
        this.watch$ = this.form.get('IdChamp').valueChanges.pipe(
            tap(id => {
                this.criterion = this.criteriaList.find(({Id}) => id === Id);
                this.rulesList.clear();
                this.defaultRulesList.clear();
                const isLst = this.criterion?.ChampTypeDonnee?.Code === 'LST';
                this.displayAddLine = !!id && !isLst;
                if (isLst) {
                    this.criterion.ListChampListeValeur.ChampListeValeurDTO.filter(({IsCloture}) => !IsCloture).forEach(({Id}) =>
                        this.rulesList.push(this.generateRulesListFormGroup({ChpLstValId: Id}))
                    );
                } else {
                    this.rulesList.push(this.generateRulesListFormGroup({}));
                }
                this.defaultRulesList.push(this.generateRulesListFormGroup({}));
                this.controls = this.getControls();
                this.displayedColumns = this.getDisplayedColumns();
            }),
        );

        this.watch$ = this.form.get('handleAge').valueChanges.pipe(
            tap(handleAge => {
                const rulesModelsList = ['RglModeleIdAgeEnfMin', 'RglModeleIdAgeEnfMax'];
                if (handleAge) {
                    this.form.get('IdTypeDateRefPond').setValidators(Validators.required);
                    rulesModelsList.forEach(k => this.form.get(k).enable());
                } else {
                    this.form.get('IdTypeDateRefPond').clearValidators();
                    rulesModelsList.forEach(k => this.form.get(k).disable());

                    this.form.patchValue({
                        IdTypeDateRefPond: this.referenceMotorDatesList.find(e => e.Code === 'OPE')?.Id || null,
                        DateRefPond: null,
                        RglModeleIdAgeEnfMin: null,
                        RglModeleIdAgeEnfMax: null
                    });
                }
            })
        );

        const IdTypeDateRefPondControl = this.form.get('IdTypeDateRefPond');
        this.watch$ = combineLatest([
            IdTypeDateRefPondControl.valueChanges.pipe(startWith(IdTypeDateRefPondControl.value as number)),
            this.referenceMotorDatesList$,
        ]).pipe(
            tap(([value, datesList]: [number, MoteurRegleTypeDateRefDto[]]) => {
                this.displayChildrenSelectedDateField = ['SAI', 'SAIINF'].some(code => {
                    return code === datesList?.find(({Id}) => Id === value)?.Code;
                });

                const dateControl = this.form.get('DateRefPond');

                let day = 1;
                let month = 1;
                if (this.displayChildrenSelectedDateField) {
                    dateControl.setValidators([SharedDaysInMonthEditComponent.validator]);
                    day = dateControl.value?.day ?? day;
                    month = dateControl.value?.month ?? month;
                } else {
                    dateControl.clearValidators();
                }

                dateControl.reset({
                    day,
                    month,
                }, {emitEvent: false});
            })
        );

        const {
            IsPondererMoisPresence: isPondererMoisPresencerControl,
            IsPondererMoisEntier: isPondererMoisEntierControl,
        } = this.form.controls;

        this.watch$ = isPondererMoisPresencerControl.valueChanges.pipe(
            startWith(isPondererMoisPresencerControl.value),
            tap(value => {
                if (value) {
                    isPondererMoisEntierControl.enable({emitEvent: false});
                } else {
                    isPondererMoisEntierControl.reset(false, {emitEvent: false});
                    isPondererMoisEntierControl.disable({emitEvent: false});
                }
            })
        );

        this.watch$ = this.form.get('IsFixe').valueChanges.pipe(
            startWith(true),
            distinctUntilChanged(isEqual),
            tap(value => {
                this.rulesList.reset(this.fb.array([]), {emitEvent: false});
                this.defaultRulesList.reset(this.fb.array([]), {emitEvent: false});
                const fieldIdControl = this.form.get('IdChamp');
                if (!value) {
                    fieldIdControl.addValidators([FormValidator.isNotNull]);
                } else {
                    fieldIdControl.reset(null, {emitEvent: false});
                    fieldIdControl.clearValidators();
                }
                fieldIdControl.updateValueAndValidity({emitEvent: false});
            })
        );
    }

    private getDisplayedColumns(): string[] {
        if (this.criterion?.ChampTypeDonnee?.Code === 'LST') {
            return [
                'Valeur',
                ...this.sharedDisplayedColumns
            ];
        }
        return [
            'De',
            'A',
            ...this.sharedDisplayedColumns
        ];
    }

    private getControls(): AbstractControl[] {
        return [...this.rulesList.controls];
    }

    private getReferenceMotorDatesList$(): Observable<void> {
        return this.store.dispatch(new SharedGetReferenceMotorDatesListAction()).pipe(
            tap(() => {
                this.referenceMotorDatesList = this.store.selectSnapshot(ReferenceYearsState.referenceMotorDatesList);
            })
        );
    }

    private generateRulesListFormGroup(value: RegleSubQuotaListeValModelDto): FormGroup {
        const {
            Id,
            ChpLstValId,
            A,
            De,
            Od,
            Conjoint,
            Enfant,
            Externe,
            ValMin,
            ValMax,
        } = value;

        return this.fb.group({
                Id: [Id ?? 0],
                ChpLstValId: [ChpLstValId || null],
                A: [A || null],
                De: [De || null],
                Od: [Od || null],
                Conjoint: [Conjoint || null],
                Enfant: [Enfant || null],
                Externe: [Externe || null],
                ValMin: [ValMin || null],
                ValMax: [ValMax || null],
            },
            {
                validators: [
                    form => (form.value.ValMin > form.value.ValMin ? {valminsupmax: true} : null),
                ]
            });
    }

    private normalize(value: ModeleRegleSubQuotaModeleDto & {
        ListeRegle: RegleSubQuotaListeValModelDto[],
        defaultValues: any[],
        handleAge: boolean,
        IsFixe: boolean,
        DateRefPond: { day: number, month: number }
    }): NormalizeType {

        const {
            handleAge,
            defaultValues,
            ListeRegle,
            IsFixe,
            IdChamp,
            DateRefPond,
            RglModeleIdAgeEnfMin,
            RglModeleIdAgeEnfMax,
            ...valuesToKeep
        } = value;

        const nullIfFixe = ['A', 'De', 'ChpLstValId'];
        ListeRegle?.forEach(rule => {
            Object.keys(rule).filter(key => key !== 'Id').forEach(key => {
                rule[key] = (IsFixe && nullIfFixe.includes(key))
                    ? null
                    : +rule[key] || null;
            });
            rule.Id = rule.Id ?? 0;
        });

        const cleanedDefaultValues = (defaultValues || []).reduce((acc, value) => {
            deleteEmptyProperties(value);
            return isEmpty(value) ? acc : acc.concat(value);
        }, []);

        const rulesEnvelopeQuotaList = IsFixe ? [] : cleanedDefaultValues.filter(e => !!e?.De || !!e?.Od || !!e?.Conjoint || !!e?.Enfant);
        let model = {
            ...valuesToKeep,
            IdChamp: IsFixe ? null : IdChamp,
            DateRefPondJour: DateRefPond?.day || null,
            DateRefPondMois: DateRefPond?.month || null,
            ListeModeleRegleSubQuotaValModele: {
                RegleSubQuotaListeValModelDTO: [
                    ...(ListeRegle ?? []),
                    ...rulesEnvelopeQuotaList
                ]
            },
            RglModeleIdAgeEnfMin: +RglModeleIdAgeEnfMin || null,
            RglModeleIdAgeEnfMax: +RglModeleIdAgeEnfMax || null,
        } as NormalizeType;

        if (arrayNotEmpty(cleanedDefaultValues)) {
            model.ListeValeursDefault = {
                anyType: [...defaultValues || []]
            };
        }

        return model;
    }

    ngOnInit(): void {
        this.form = this.fb.group({
                Id: [0],
                IsFixe: [true],
                IdChamp: [null],
                IdTypeDateRefPond: [null],
                ListeRegle: this.fb.array([]),
                RglModeleIdAgeEnfMin: [{value: null, disabled: true}, [Validators.min(0)]],
                RglModeleIdAgeEnfMax: [{value: null, disabled: true}, [Validators.min(0)]],
                RglModeleIdEnfAch: [false],
                RglModeleIdEnfDir: [false],
                RglModeleIdEnfHan: [false],
                IsPondererMoisEntier: [false],
                IsPondererMoisPresence: [false],
                IsPondererTempsTravail: [false],
                IsNominatif: [false],
                DateRefPond: [null],
                handleAge: [false],
                defaultValues: this.fb.array([]),
            },
            {
                validators: [
                    form => (!!form.value.handleAge
                        && !!form.value.RglModeleIdAgeEnfMin && !!form.value.RglModeleIdAgeEnfMax
                        && form.value.RglModeleIdAgeEnfMin > form.value.RglModeleIdAgeEnfMax ? {ageminsupmax: true} : null
                    ),
                    form => (!!form.value.handleAge && !form.value.RglModeleIdAgeEnfMin && !form.value.RglModeleIdAgeEnfMax ? {required: true} : null),
                    form => (form.value.ListeRegle.every(rule => ['Od', 'Conjoint', 'Enfant', 'Externe'].every(mandatory => !rule[mandatory]))
                    && (this.isFixe || !!form.value.IdChamp)
                        ? {invalidsubventions: true}
                        : null)
                ]
            });

        this.controls = this.getControls();
        this.displayedColumns = this.getDisplayedColumns();

        this.initSubscriptions();
    }
}
